import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import Commitments from "Pages/Common/Projects/Create/Components/Commitments";
import ProjectModal from "Pages/Common/Projects/Create/Components/Modal";
import RealizationAddress from "Pages/Common/Projects/Create/Components/RealizationAddress";

import { HTTPErrorCallback } from "Helpers/Error";
import { presentationExtensions } from "Helpers/File";
import { currencyMask, transformIntoArrayOptions } from "Helpers/Format";
import { RELATED_AREA_OPTIONS, relatedAreasToOptions } from "Helpers/Project";
import { clearMask } from "Helpers/Util";

import UseFile from "Hooks/UseFile";
import UseGlobalConfirm from "Hooks/UseGlobalConfirm";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import DateValidity from "Components/Admin/DateValidity";
import FileUploader from "Components/Admin/FileUploader";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import Select from "Components/Admin/Form/Select";
import GetIcon from "Components/Global/Icons";

import * as S from "./styles";

import { useTheme } from "styled-components";

// Ids fixos de select_project_statuses (espelham src/config/selectProjectStatuses.js
// no backend). "Em montagem" é o status inicial de toda seletiva nova.
const STATUS_IN_ASSEMBLY = 1;
const STATUS_SENT = 2;

const formSchema = z.object({
  name: z.string().min(3, "Nome deve ter ao menos 3 caracteres"),
  start_at: z.string().min(1, "Informe a data de início"),
  finish_at: z.string().min(1, "Informe a data de fim"),
});

const toDateInput = (value) => (value ? String(value).slice(0, 10) : "");

const SeletivaForm = ({
  initialData,
  defaultSponsorId,
  onSubmit,
  onCancel,
  submitLabel,
  loading,
  // Quantos projetos desta seletiva já estão "Apresentado" ou adiante. Usado só
  // para avisar (não bloquear) quem publica uma seletiva vazia.
  presentedProjectsCount = 0,
  // Chamado após gerar a apresentação, para a tela recarregar a seletiva.
  onPresentationGenerated,
}) => {
  const theme = useTheme();
  const { api } = UseServices();
  const { uploadFile } = UseFile();
  const { showNotification } = UseNotification();
  const { addGlobalConfirm } = UseGlobalConfirm();

  // Apresentação da seletiva (select_projects.presentation_src). Um arquivo.
  // `presentationChanged`: a chave só vai no payload quando o admin mexe no
  // campo — reenviar o valor da tela gravaria a URL ASSINADA (que expira).
  const [presentation, setPresentation] = useState([]);
  const [presentationChanged, setPresentationChanged] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const [sponsorOptions, setSponsorOptions] = useState([]);
  const [selectedSponsor, setSelectedSponsor] = useState(null);

  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [selectedRelatedAreas, setSelectedRelatedAreas] = useState([]);

  const [minValue, setMinValue] = useState("");

  const [commitments, setCommitments] = useState([]);

  const [realizationAddress, setRealizationAddress] = useState({});
  const [realizationAddressIdhs, setRealizationAddressIdhs] = useState({});
  const [realizationAddressModalOpen, setRealizationAddressModalOpen] =
    useState(false);
  const [currentUf, setCurrentUf] = useState(null);

  const methods = useForm({ resolver: zodResolver(formSchema) });
  const { register, setValue } = methods;

  useEffect(() => {
    api
      .post("/sponsors/list", { filters: {} })
      .then(({ data }) =>
        setSponsorOptions(
          (data?.sponsors || []).map((sponsor) => ({
            value: sponsor.id,
            label: sponsor.name,
          })),
        ),
      )
      .catch((error) => HTTPErrorCallback(error));

    api
      .get("/select-projects/get-statuses")
      .then(({ data }) =>
        setStatusOptions(
          transformIntoArrayOptions(data, "id", "display_name") || [],
        ),
      )
      .catch((error) => HTTPErrorCallback(error));

    api
      .get("/projects/get-project-categories")
      .then(({ data }) =>
        setCategoryOptions(
          (data || []).map((category) => ({
            value: category.id,
            label: category.display_name,
          })),
        ),
      )
      .catch((error) => HTTPErrorCallback(error));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!initialData) return;

    setValue("name", initialData.name || "");
    setValue("start_at", toDateInput(initialData.start_at));
    setValue("finish_at", toDateInput(initialData.finish_at));

    if (initialData.sponsor_id)
      setSelectedSponsor({
        value: initialData.sponsor_id,
        label:
          initialData.sponsor?.name || `Patrocinador ${initialData.sponsor_id}`,
      });

    if (Array.isArray(initialData.category_ids) && categoryOptions.length)
      setSelectedCategories(
        categoryOptions.filter((option) =>
          initialData.category_ids.includes(option.value),
        ),
      );

    if (Array.isArray(initialData.related_areas))
      setSelectedRelatedAreas(relatedAreasToOptions(initialData.related_areas));

    if (initialData.min_contribution_value != null)
      setMinValue(
        currencyMask(
          String(Math.round(Number(initialData.min_contribution_value) * 100)),
        ),
      );

    if (Array.isArray(initialData.ods)) setCommitments(initialData.ods);

    if (
      initialData.realization_address &&
      typeof initialData.realization_address === "object"
    )
      setRealizationAddress(initialData.realization_address);

    if (
      initialData.realization_address_idh &&
      typeof initialData.realization_address_idh === "object"
    )
      setRealizationAddressIdhs(initialData.realization_address_idh);

    // O anexo chega do backend como URL assinada (só para exibição/download).
    setPresentation(
      initialData.presentation_src
        ? [
            {
              file_src: initialData.presentation_src,
              file_name: "Apresentação da seletiva",
            },
          ]
        : [],
    );
    setPresentationChanged(false);
    // eslint-disable-next-line
  }, [initialData, categoryOptions]);

  // Selecionar e remover (o "X" do próprio FileUploader) passam por aqui.
  const handlePresentationChange = (files) => {
    setPresentation(files || []);
    setPresentationChanged(true);
  };

  // Gera o .pptx no backend e reanexa. NÃO passa pelo submit do formulário: a
  // rota grava apenas presentation_src, então alterações não salvas na tela
  // não são nem persistidas nem descartadas.
  const runGeneration = () => {
    setGenerating(true);

    api
      .post("/select-projects/generate-presentation", { id: initialData.id })
      .then(({ data }) => {
        showNotification(
          "success",
          "Sucesso!",
          data?.message || "Apresentação gerada com sucesso!",
        );
        // Recarrega a seletiva: o useEffect de initialData repopula o campo
        // com o arquivo novo, sem o admin precisar atualizar a página.
        onPresentationGenerated?.();
      })
      .catch((error) => HTTPErrorCallback(error))
      .finally(() => setGenerating(false));
  };

  // Envia a apresentação ao patrocinador. Como o "Gerar apresentação", usa o
  // que está SALVO — o e-mail não leva alterações abertas no formulário.
  const runSendEmail = () => {
    setSending(true);

    api
      .post("/select-projects/send-presentation-email", { id: initialData.id })
      .then(({ data }) => {
        showNotification(
          "success",
          "Sucesso!",
          data?.message || "E-mail enviado ao patrocinador!",
        );
      })
      .catch((error) => HTTPErrorCallback(error))
      .finally(() => setSending(false));
  };

  const handleSendEmail = () => {
    // Confirmação sempre: a ação sai da plataforma e o link vale 3 meses.
    addGlobalConfirm({
      type: "critical",
      title: "Enviar a apresentação ao patrocinador?",
      message:
        "Os usuários da empresa patrocinadora receberão um e-mail com os dados da seletiva e um link de acesso à apresentação válido por 3 meses. Valem os dados já salvos — alterações abertas neste formulário não entram no e-mail.",
      confirmText: "Sim, enviar",
      denyText: "Cancelar",
      onClickConfirm: runSendEmail,
    });
  };

  // Pré-requisitos do envio (o backend também barra): sem anexo não há o que
  // linkar, e em "Em montagem" o patrocinador nem enxerga a seletiva.
  const isSent = Number(selectedStatus?.value) === STATUS_SENT;
  const hasPresentation = presentation.length > 0;

  const sendEmailBlockedReason = !hasPresentation
    ? "Anexe ou gere a apresentação antes de enviar"
    : !isSent
      ? 'Publique a seletiva (status "Enviado") para enviar o e-mail'
      : null;

  const handleGenerate = () => {
    // Já existe anexo (gerado antes ou subido à mão): confirmar antes de
    // substituir, para não descartar uma versão revisada manualmente.
    if (presentation.length > 0) {
      addGlobalConfirm({
        type: "critical",
        title: "Substituir a apresentação atual?",
        message:
          "Já existe uma apresentação anexada nesta seletiva. Gerar uma nova vai substituir o arquivo atual.",
        confirmText: "Sim, gerar nova",
        denyText: "Não, manter a atual",
        onClickConfirm: runGeneration,
      });

      return;
    }

    runGeneration();
  };

  // Status da seletiva: na criação nasce em "Em montagem" (rascunho); na edição,
  // abre no valor atual.
  useEffect(() => {
    if (!statusOptions.length) return;

    const current = Number(initialData?.status_id ?? STATUS_IN_ASSEMBLY);

    setSelectedStatus(
      statusOptions.find((option) => Number(option.value) === current) || null,
    );
    // eslint-disable-next-line
  }, [statusOptions, initialData]);

  // Pré-seleciona o patrocinador quando vindo com ?sponsor_id (ex.: criar
  // seletiva a partir do detalhe do patrocinador). Não se aplica à edição, que
  // usa initialData.
  useEffect(() => {
    if (initialData || !defaultSponsorId || !sponsorOptions.length) return;

    const option = sponsorOptions.find(
      (item) => String(item.value) === String(defaultSponsorId),
    );

    if (option) setSelectedSponsor(option);
    // eslint-disable-next-line
  }, [sponsorOptions, defaultSponsorId, initialData]);

  const submitRealizationAddress = (new_cities, idhs) => {
    if (!currentUf) return;

    const uf_cities = [
      ...new Set([
        ...(realizationAddress[currentUf.label] || []),
        ...(new_cities || []).map((city) =>
          typeof city === "string" ? city : city.value,
        ),
      ]),
    ];

    setRealizationAddress({
      ...realizationAddress,
      [currentUf.label]: uf_cities,
    });

    if (idhs) setRealizationAddressIdhs((prev) => ({ ...prev, ...idhs }));

    setCurrentUf(null);
    setRealizationAddressModalOpen(false);
  };

  const removeCity = (uf, city) => {
    const next = {
      ...realizationAddress,
      [uf]: realizationAddress[uf].filter((c) => c !== city),
    };

    if (next[uf].length === 0) delete next[uf];

    setRealizationAddress(next);
  };

  const submit = async (data) => {
    if (!selectedSponsor?.value) {
      showNotification("danger", "Atenção!", "Selecione um patrocinador.");
      return;
    }

    if (!selectedCategories.length) {
      showNotification(
        "danger",
        "Atenção!",
        "Selecione ao menos uma Lei de Incentivo.",
      );
      return;
    }

    const cleanValue = clearMask(minValue);

    const payload = {
      ...(initialData?.id ? { id: initialData.id } : {}),
      sponsor_id: selectedSponsor.value,
      name: data.name,
      start_at: data.start_at,
      finish_at: data.finish_at,
      status_id: selectedStatus?.value ?? STATUS_IN_ASSEMBLY,
      category_ids: selectedCategories.map((category) => category.value),
      related_areas: selectedRelatedAreas.map((area) => area.value),
      min_contribution_value: cleanValue ? Number(cleanValue) / 100 : null,
      realization_address: realizationAddress,
      realization_address_idh: realizationAddressIdhs,
      ods: commitments,
    };

    // Anexo: só entra no payload se o admin mexeu no campo. Ausente = manter o
    // arquivo atual; null = remover.
    if (presentationChanged) {
      const file = presentation[0];

      payload.presentation_src =
        file instanceof File ? await uploadFile(file) : null;
    }

    // Publicar ("Enviado") sem nenhum projeto apresentado: avisa, mas não impede —
    // o admin pode ter razões para liberar a seletiva antes de apresentar projetos.
    if (
      Number(payload.status_id) === STATUS_SENT &&
      Number(presentedProjectsCount) === 0
    ) {
      addGlobalConfirm({
        type: "critical",
        title: "Publicar seletiva sem projetos apresentados?",
        message:
          "Nenhum projeto apresentado nesta seletiva. O patrocinador passará a vê-la, mas sem projetos.",
        confirmText: "Sim, publicar",
        denyText: "Não, revisar antes",
        onClickConfirm: () => onSubmit(payload),
      });

      return;
    }

    onSubmit(payload);
  };

  return (
    <S.Wrapper>
      {realizationAddressModalOpen && (
        <ProjectModal
          title="Adicionar Locais de Realização"
          onSubmit={submitRealizationAddress}
          modalOpen={realizationAddressModalOpen}
          setModalOpen={setRealizationAddressModalOpen}
          showButtons={false}
        >
          <RealizationAddress
            currentUf={currentUf}
            setCurrentUf={setCurrentUf}
            setModalOpen={setRealizationAddressModalOpen}
            onSubmit={submitRealizationAddress}
            variant="light"
          />
        </ProjectModal>
      )}

      <FormWrapper {...methods} submit={submit}>
        <Input
          {...register("name")}
          type="text"
          label="Nome da Seletiva:"
          placeholder="Nome do processo seletivo"
          required
        />

        <Select
          name="sponsor_id"
          label="Patrocinador:"
          placeholder="Selecione um patrocinador"
          options={sponsorOptions}
          value={selectedSponsor}
          onChange={setSelectedSponsor}
          required
        />

        <Select
          name="status_id"
          label="Status da Seletiva:"
          placeholder="Selecione o status"
          options={statusOptions}
          value={selectedStatus}
          onChange={setSelectedStatus}
          required
        />

        <S.FieldHint>
          <strong>Em montagem:</strong> só o administrador vê — o patrocinador
          não enxerga a seletiva nem os projetos dela. <strong>Enviado:</strong>{" "}
          disponível para o patrocinador. <strong>Finalizada:</strong> continua
          visível para ele, no histórico.
        </S.FieldHint>

        <DateValidity register={register} required />

        <Select
          name="category_ids"
          label="Lei de Incentivo:"
          placeholder="Selecione as leis"
          options={categoryOptions}
          value={selectedCategories}
          onChange={setSelectedCategories}
          isMulti
          required
        />

        <Select
          name="related_areas"
          label="Área relacionada:"
          placeholder="Selecione as áreas"
          options={RELATED_AREA_OPTIONS}
          value={selectedRelatedAreas}
          onChange={setSelectedRelatedAreas}
          isMulti
        />

        <Input
          name="min_contribution_value"
          type="text"
          label="Valor mínimo de aporte por projeto (R$):"
          placeholder="0,00"
          value={minValue}
          onChange={(e) => setMinValue(currencyMask(e.target.value))}
        />

        <S.Territory>
          <label>Território (UF / Cidades):</label>

          <Button
            type="button"
            outline
            text="Selecionar Locais de Realização"
            onClick={() => setRealizationAddressModalOpen(true)}
            width="280px"
          />

          {Object.keys(realizationAddress).map((uf) => (
            <S.Uf key={uf}>
              <h4>{uf}</h4>
              <S.Cities>
                {realizationAddress[uf].map((city) => (
                  <S.City key={city} variant="light">
                    {city}{" "}
                    {realizationAddressIdhs[city]
                      ? `(IDH-M: ${realizationAddressIdhs[city]})`
                      : null}
                    <GetIcon
                      name="X"
                      color={theme.colors.neutral.black}
                      onClick={() => removeCity(uf, city)}
                    />
                  </S.City>
                ))}
              </S.Cities>
            </S.Uf>
          ))}
        </S.Territory>

        <Commitments
          commitments={commitments}
          setCommitments={setCommitments}
          variant="light"
        />

        <S.PresentationField>
          <S.PresentationHeader>
            <label>Apresentação da seletiva:</label>

            {/* Só na edição: sem seletiva salva não há projetos para montar. */}
            {initialData?.id && (
              <S.PresentationActions>
                {/* O `title` fica no WRAPPER, não no botão: navegador não
                    dispara mouseover em <button disabled>, então a tooltip
                    nativa do botão nunca aparecia. */}
                <S.TooltipWrapper title={sendEmailBlockedReason || undefined}>
                  <Button
                    type="button"
                    outline
                    text={sending ? "Enviando..." : "Enviar por e-mail"}
                    onClick={handleSendEmail}
                    disabled={
                      sending ||
                      generating ||
                      loading ||
                      Boolean(sendEmailBlockedReason)
                    }
                  />
                </S.TooltipWrapper>

                <Button
                  type="button"
                  outline
                  text={generating ? "Gerando..." : "Gerar apresentação"}
                  onClick={handleGenerate}
                  disabled={generating || sending || loading}
                />
              </S.PresentationActions>
            )}
          </S.PresentationHeader>

          {/* Diz por que o envio está bloqueado — o botão desabilitado sozinho
              não explica, e o `title` só aparece no hover. */}
          {initialData?.id && sendEmailBlockedReason && (
            <S.FieldHint>{sendEmailBlockedReason}.</S.FieldHint>
          )}

          <FileUploader
            label=""
            loading={loading}
            allowedExtensions={presentationExtensions}
            setFiles={handlePresentationChange}
            files={presentation}
            showFileName
            // Opt-in: sem o prefixo o link é só o nome do arquivo e o admin não
            // percebe que é clicável (pedido do cliente, 18/08/2026). O
            // FileUploader serve 16 telas, então o padrão não muda.
            downloadLabel="Fazer download"
          />

          <S.FieldHint>
            Arquivo apresentado ao patrocinador junto da seletiva (PDF,
            PowerPoint ou Word). Fica disponível para download assim que a
            seletiva estiver em <strong>Enviado</strong>.
          </S.FieldHint>
        </S.PresentationField>

        <S.FormActions>
          {onCancel && (
            <Button
              type="button"
              outline
              text="Cancelar"
              onClick={onCancel}
              disabled={loading}
            />
          )}
          <Button
            type="submit"
            text={submitLabel || "Salvar"}
            disabled={loading}
          />
        </S.FormActions>
      </FormWrapper>
    </S.Wrapper>
  );
};

export default SeletivaForm;
